import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useViewlogies } from 'context';
import { createPaymentIntent, submitOrder } from 'services/carePackage';
import { getViewerContactCookie, setViewerContactCookie, getGuestCookie, setGuestCookie } from 'services/guest';
import config from 'config/default';

const stripePromise = loadStripe(config.stripePublishableKey);

const PaymentForm = ({
    onSuccess,
    color,
    organizationId,
    eventId,
    orderNumber,
    message,
    fromName,
    name,
    email,
    phone,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const alreadyOnGuestList = !!getGuestCookie(eventId);

    const handleSubmit = async e => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setLoading(true);
        setError(null);

        const { error: stripeError } = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
        });

        if (stripeError) {
            setError(stripeError.message);
            setLoading(false);
            return;
        }

        setViewerContactCookie({ name, email });

        // payment already succeeded — a failed contact-details update must not block confirmation
        submitOrder(orderNumber, { message, fromName, contact: { name, email, phone } }).catch(() => {});

        // the API adds the buyer to the guest list server-side; mirror that in the cookie
        if (!alreadyOnGuestList && name && email) {
            setGuestCookie(eventId);
        }

        onSuccess({ orderNumber });
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement />
            {error && <div className="care-package-error">{error}</div>}
            <div className="care-package-actions">
                <button
                    type="submit"
                    className="care-package-btn"
                    style={{ backgroundColor: color }}
                    disabled={!stripe || loading}
                >
                    {loading ? 'Processing\u2026' : 'Pay'}
                </button>
            </div>
        </form>
    );
};

const SubmitPayment = ({ product, message, fromName, onSuccess }) => {
    const {
        event: { organizationId, id: eventId, color },
    } = useViewlogies();

    const savedContact = getViewerContactCookie();
    const alreadyOnGuestList = !!getGuestCookie(eventId);

    const [name, setName] = useState(savedContact?.name || fromName || '');
    const [email, setEmail] = useState(savedContact?.email || '');
    const [phone, setPhone] = useState('');
    const [clientSecret, setClientSecret] = useState(null);
    const [orderNumber, setOrderNumber] = useState(null);
    const [paymentIntentError, setPaymentIntentError] = useState(null);

    useEffect(() => {
        const init = async () => {
            try {
                const res = await createPaymentIntent(organizationId, eventId, product.id, { message, fromName });
                if (res?.data?.clientSecret) {
                    setClientSecret(res.data.clientSecret);
                    setOrderNumber(res.data.orderNumber);
                } else {
                    setPaymentIntentError('Unable to initialize payment. Please try again.');
                }
            } catch {
                setPaymentIntentError('Unable to initialize payment. Please try again.');
            }
        };
        init();
        // message/fromName are captured at intent creation; edits require going back, which remounts this step
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, eventId, product.id]);

    return (
        <div className="care-package-submit-payment">
            <h3 className="care-package-section-title">
                Contact details{' '}
                <span className="care-package-section-subtitle">(we&apos;ll update you about your order delivery)</span>
            </h3>
            <input
                className="care-package-input"
                placeholder="First and Last Name"
                value={name}
                onChange={e => setName(e.target.value)}
            />
            {!alreadyOnGuestList && (
                <p className="care-package-guest-list-note">
                    We&apos;ll also automatically add your name to the Guest List
                </p>
            )}
            <input
                className="care-package-input"
                placeholder="Email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
            />
            <input
                className="care-package-input"
                placeholder="Cell Phone Number"
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
            />

            <h3 className="care-package-section-title">Payment method</h3>

            {paymentIntentError && <div className="care-package-error">{paymentIntentError}</div>}

            {!clientSecret && !paymentIntentError && (
                <div className="care-package-loading">Loading payment&hellip;</div>
            )}

            {clientSecret && (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <PaymentForm
                        onSuccess={onSuccess}
                        color={color}
                        organizationId={organizationId}
                        eventId={eventId}
                        orderNumber={orderNumber}
                        message={message}
                        fromName={fromName}
                        name={name}
                        email={email}
                        phone={phone}
                    />
                </Elements>
            )}
        </div>
    );
};

export default SubmitPayment;
